import appraisalLetterTemplates from "./appraisal-letter-templates.json";

function requireElement<T extends Element>(parent: ParentNode, selector: string): T {
  const element = parent.querySelector<T>(selector);
  if (!element) throw new Error(`SQE appraisal terms are missing ${selector}.`);
  return element;
}

/** The common clauses are shared with the existing letter; the SQE PDF supplies these variations. */
export function createSqeAppraisalTermsHtml(employeeName: string, location?: string): string {
  const template = new DOMParser().parseFromString(appraisalLetterTemplates.dynamic, "text/html");
  const terms = requireElement<HTMLElement>(template, ".terms");
  const sectionList = requireElement<HTMLOListElement>(terms, "ol");
  const sections = Array.from(sectionList.children);
  // Pagination adds each clause number to its heading. Suppress the ordered
  // list's own marker on the source nodes so cloned PDF pages cannot show it too.
  sectionList.style.listStyleType = "none";
  sections.forEach((item) => { (item as HTMLElement).style.listStyleType = "none"; });
  const section = (heading: string): Element => {
    const match = sections.find((item) => item.querySelector("h4")?.textContent?.trim().startsWith(heading));
    if (!match) throw new Error(`SQE appraisal terms are missing ${heading}.`);
    return match;
  };

  requireElement<HTMLElement>(terms, ":scope > p").textContent =
    "It is hereby agreed that you will abide by the following terms and conditions with SQE Labs Inc.";
  requireElement<HTMLElement>(section("Place of work"), "p").textContent =
    `Your assigned work location is ${location?.trim() || "Chandigarh (SAS Nagar)"}. The Company retains the right to relocate you within the designated work area.`;
  requireElement<HTMLElement>(section("Notice Period"), "p").textContent =
    "For discontinuation of service, you are required to serve a notice period of Sixty (60) Days.";

  // Clause 5.2 names the employer explicitly in both letters.
  const suitability = requireElement<HTMLElement>(section("Implied Warranty of Suitability"), ".sub-bullet:nth-of-type(2) > span:last-child");
  suitability.textContent = "In addition the Employee guarantees that he will meet the standards of performance and output required by SQE Labs.";
  const terminationNotice = requireElement<HTMLElement>(section("Termination of Employment"), ".sub-bullet > span:last-child");
  const noticePeriod = requireElement<HTMLElement>(terminationNotice, "b[th\\:text]");
  noticePeriod.textContent = "Sixty (60) Days";
  noticePeriod.removeAttribute("th:text");
  terminationNotice.innerHTML = terminationNotice.innerHTML.replace(
    /This clause is solely applicable at the discretion of the Company\.\s*/i,
    "",
  );

  requireElement<HTMLElement>(section("Confidentiality"), "p").textContent =
    "Salary-related information is Company Confidential for the sole use of the employee and cannot be shared with any other person directly or indirectly. Violation to this will be treated as a breach of the employment contract and will be dealt with accordingly. The appraised amount shall stand null and void in case the employee resigns.";
  const note = Array.from(terms.children).find((child) => child.tagName === "P" && child.textContent?.includes("3 months"));
  if (!note) throw new Error("SQE appraisal revision note is missing.");
  note.innerHTML = "<i><b>Note:</b> If the employee resigns within 3 months post the implementation of revision i.e., from the date of credit of arrears of incremented salary, the revised salary will be reverted at the time of F&amp;F calculation.</i>";

  const declarationName = requireElement<HTMLTableCellElement>(terms, "table tr td:nth-child(2)");
  declarationName.textContent = employeeName;
  // The shared template splits clauses 4.4 and 12.2 into extra paragraphs.
  // Those fragments have no clause number, so their text would occupy the
  // narrow number column in the SQE letter's two-column clause layout.
  terms.querySelectorAll<HTMLElement>(".sub-bullet").forEach((paragraph) => {
    const spans = Array.from(paragraph.querySelectorAll(":scope > span"));
    if (spans.length === 2 || spans.slice(0, -1).some((span) => span.textContent?.trim())) return;

    const previous = paragraph.previousElementSibling;
    const previousText = previous?.classList.contains("sub-bullet")
      ? previous.querySelector(":scope > span:last-child")
      : null;
    const continuationText = spans.at(-1)?.textContent?.trim();
    if (!previousText || !continuationText) return;

    previousText.appendChild(template.createTextNode(` ${continuationText}`));
    paragraph.remove();
  });
  terms.querySelectorAll("[th\\:text]").forEach((element) => element.removeAttribute("th:text"));
  terms.querySelectorAll(".page-break").forEach((element) => element.remove());
  return terms.innerHTML.replace(/â€™|â€˜|â€œ|â€/g, (value) => ({
    "â€™": "’", "â€˜": "‘", "â€œ": "“", "â€": "”",
  })[value] ?? value);
}
